import { HttpErrorResponse } from '@angular/common/http';

import { createAction, props } from '@ngrx/store';

import { Screen } from '@core/interfaces';

import { ErrorResponse } from '#types/core';
import { Filters, Sorts } from '#types/grid';
import { MultipleListOfValuesResponse } from '#types/list';
import {
  ReportCompletedResponseBody,
  ReportDownloadResponseBody,
  ReportGenerateResponseBody,
  ReportGetParamsResponseBody,
  ReportLookupResponseBody,
  ReportParameter,
  ReportRefreshResponseBody,
  ReportSetParamsResponseBody,
  ReportStatusResponseBody,
} from '#types/report';

//
// report / request lists
//

export const RequestLists = createAction('[Report] - Request Lists');

//
// report lists
//

export const RequestReportLists = createAction('[Report.Lists] - Request Report Lists');
export const RequestReportListsSuccess = createAction(
  '[Report.Lists] - Request Report Lists Success',
  props<{ lists: MultipleListOfValuesResponse }>()
);
export const RequestReportListsFailure = createAction('[Report.Lists] - Request Report Lists Failure', props<{ error: ErrorResponse }>());

//
// report / lookup
//

export const LookupInitialize = createAction('[Report.Lookup] - Initialize', props<{ screen: Screen }>());

export const LookupRequestData = createAction('[Report.Lookup] - Grid Request Data', props<{ id: string }>());

export const LookupRequestDataWithParameters = createAction(
  '[Report.Lookup] - Grid Request Data With Parameters',
  props<{ id: string; filters: Filters; page: number; first: number; rows: number; sorts: Sorts }>()
);

export const LookupRequestDataWithFilters = createAction(
  '[Report.Lookup] - Grid Request Data With Filters',
  props<{ id: string; filters: Filters }>()
);

export const LookupRequestDataSuccess = createAction(
  '[Report.Lookup] - Grid Request Data Success',
  props<{ id: string; data: ReportLookupResponseBody }>()
);

export const LookupRequestDataFailure = createAction('[Report.Lookup] - Grid Request Data Failure', props<{ id: string }>());

//
// report / get parameters
//

export const RequestReportGetParameters = createAction('[Report] - Get Parameters Request', props<{ reportName: string }>());
export const RequestReportGetParametersSuccess = createAction(
  '[Report] - Get Parameters Request Success',
  props<{ reportName: string; data: ReportGetParamsResponseBody }>()
);
export const RequestReportGetParametersFailure = createAction(
  '[Report] - Get Parameters Request Failure',
  props<{ reportName: string; error: HttpErrorResponse }>()
);

export const RequestReportGetParametersRefresh = createAction('[Report] - Get Parameters Refresh Request', props<{ reportName: string }>());
export const RequestReportGetParametersRefreshSuccess = createAction(
  '[Report] - Get Parameters Refresh Request Success',
  props<{ reportName: string; data: ReportGetParamsResponseBody }>()
);
export const RequestReportGetParametersRefreshFailure = createAction(
  '[Report] - Get Parameters Refresh Request Failure',
  props<{ reportName: string; error: HttpErrorResponse }>()
);

//
// report / clear parameters
//

export const RequestReportClearParameters = createAction('[Report] - Clear Parameters');

//
// report / set parameters
//

export const RequestReportSetParameters = createAction(
  '[Report] - Set Parameters Request',
  props<{ format: string; parameters: ReportParameter[] }>()
);
export const RequestReportSetParametersSuccess = createAction(
  '[Report] - Set Parameters Request Success',
  props<{ format: string; data: ReportSetParamsResponseBody }>()
);
export const RequestReportSetParametersFailure = createAction(
  '[Report] - Set Parameters Request Failure',
  props<{ error: HttpErrorResponse }>()
);

//
// report / run report
//

export const RequestReportRunReport = createAction('[Report] - Run Report Request');
export const RequestReportRunReportSuccess = createAction(
  '[Report] - Run Report Request Success',
  props<{ data: ReportGenerateResponseBody }>()
);
export const RequestReportRunReportFailure = createAction('[Report] - Run Report Request Failure', props<{ error: HttpErrorResponse }>());

//
// report / download report
//

export const RequestReportDownload = createAction(
  '[Report.Download] - Download Report Request',
  props<{ requestId: string; format: string; fileName: string }>()
);

export const RequestReportDownloadSuccess = createAction(
  '[Report.Download] - Download Report Request Success',
  props<{ requestId: string; fileName: string; data: ReportDownloadResponseBody }>()
);

export const RequestReportDownloadFailure = createAction(
  '[Report.Download] - Download Report Request Failure',
  props<{ error: HttpErrorResponse }>()
);

//
// report / completed
//

export const CompletedInitialize = createAction('[Report.Completed] - Initialize', props<{ screen: Screen }>());

export const CompletedRequestData = createAction('[Report.Completed] - Grid Request Data', props<{ id: string }>());

export const CompletedRequestDataWithFilters = createAction(
  '[Report.Completed] - Grid Request Data With Filters',
  props<{ id: string; filters: Filters }>()
);

export const CompletedRequestDataWithParameters = createAction(
  '[Report.Completed] - Grid Request Data With Parameters',
  props<{ id: string; filters: Filters; page: number; first: number; rows: number; sorts: Sorts }>()
);

export const CompletedRequestDataSuccess = createAction(
  '[Report.Completed] - Grid Request Data Success',
  props<{ id: string; data: ReportCompletedResponseBody }>()
);

export const CompletedRequestDataFailure = createAction('[Report.Completed] - Grid Request Data Failure', props<{ id: string }>());

//
// report / refresh
//

export const RequestReportRefresh = createAction('[Report] - Refresh Request');
export const RequestReportRefreshSuccess = createAction('[Report] - Refresh Request Success', props<{ data: ReportRefreshResponseBody }>());
export const RequestReportRefreshFailure = createAction('[Report] - Refresh Request Failure', props<{ error: HttpErrorResponse }>());

//
// report / status
//

export const RequestReportStatus = createAction('[Report] - Report Status Request', props<{ pendingRequests: string[] }>());
export const RequestReportStatusSuccess = createAction(
  '[Report] - Report Status Request Success',
  props<{ data: ReportStatusResponseBody }>()
);
export const RequestReportStatusFailure = createAction('[Report] - Report Status Request Failure', props<{ error: HttpErrorResponse }>());
export const RequestReportStatusDone = createAction('[Report] - Report Status Request Done');

//
// pending reports
//

export const PendingReportAdd = createAction('[Report.Pending] - Add', props<{ requestId: string; format: string; filename: string }>());
export const PendingReportRemove = createAction('[Report.Pending] - Remove', props<{ requestId: string }>());
export const PendingReportClear = createAction('[Report.Pending] - Clear');
